import React, { useEffect, useState } from 'react';
import { PhoneCall, PhoneOff, X, AlertCircle } from 'lucide-react';
import { useStudioLineContext } from '../context/StudioLineContext';
import { useRingtone } from '../hooks/useRingtone';
import {
  preloadRealtimeKit,
  prewarmCall,
  releasePrewarm,
  releaseStalePrewarms,
} from '../hooks/useStudioCallVoice';

/**
 * المكالمات الواردة — للمسؤول وحده، ومعلّقة فوق التطبيق كله.
 *
 * وضعها داخل لوحة التحكم يعني ألا يراها إلا من فتحها، والمكالمة لا
 * تنتظر. فهي هنا تطفو فوق أي شاشة، وتختفي تماماً حين لا أحد على الخط
 * كي لا تزاحم الواجهة بلا سبب.
 */

/**
 * مدّة الانتظار.
 *
 * تُحسب من وقت الخادم، وساعة الجهاز قد تخالفه بثوانٍ أو دقائق — فنحرس
 * من السالب حتى لا يظهر انتظار بالسالب، ونقبل الانحراف هنا لأن الرقم
 * إرشادي لا دقيق.
 */
const since = (at) => {
  const s = Math.max(0, Math.floor((Date.now() - at) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const IncomingCallDock = () => {
  const line = useStudioLineContext();
  const { queue, active, others = [], isAdmin, errorMessage, clearError } = line;

  // رنين ما دام أحد ينتظر الردّ، مع تحميل مسبق لحزم الصوت
  useRingtone('/ring-in.mp3', isAdmin && !active && queue.length > 0, { volume: 0.55 });

  useEffect(() => {
    if (isAdmin && queue.length > 0) {
      preloadRealtimeKit().catch(() => {});
    }
  }, [isAdmin, queue.length]);

  /*
    جلسة صوت جاهزة لأول مكالمة في الطابور، قبل أن يضغط المذيع «ردّ».

    بدونها يبدأ المذيع من الصفر عند الردّ، فلا يسمعه المتصل إلا بعد
    ١١–١٣ ثانية (مقيسةً). والأولى وحدها: هي التي يُردّ عليها غالباً،
    وتجهيز الطابور كله يحجز جلسة لكل منتظر.
  */
  const nextCallId = isAdmin && !active ? queue[0]?.id ?? null : null;
  useEffect(() => {
    if (!nextCallId) return;
    prewarmCall(nextCallId);
    return () => releasePrewarm(nextCallId);
  }, [nextCallId]);

  // مكالمة ردّ عليها زميل تخرج من الطابور: نغادر غرفتها فوراً بلا مهلة
  const keepKey = isAdmin ? [...queue.map((c) => c.id), active?.id ?? ''].join(',') : null;
  useEffect(() => {
    if (keepKey === null) return;
    releaseStalePrewarms(new Set(keepKey.split(',').filter(Boolean)));
  }, [keepKey]);

  const [collapsed, setCollapsed] = useState(false);
  const [, tick] = useState(0);

  // مؤقّت الانتظار يحتاج إعادة رسم كل ثانية
  useEffect(() => {
    if (queue.length === 0) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [queue.length]);

  useEffect(() => {
    if (!errorMessage) return;
    const t = setTimeout(clearError, 5000);
    return () => clearTimeout(t);
  }, [errorMessage, clearError]);

  // مكالمة جديدة تستحق أن تُفتح اللوحة ولو طواها المسؤول قبل قليل
  useEffect(() => {
    if (queue.length > 0 || active) setCollapsed(false);
  }, [queue.length, active]);

  // المكالمة الجارية تملأ الشاشة (StudioCallScreen)، فلا تُعرض هنا
  if (!isAdmin || active || queue.length === 0) return null;

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed bottom-24 left-4 z-40 h-11 px-4 rounded-full bg-primary text-primary-ink text-xs font-semibold shadow-lift flex items-center gap-2"
      >
        <PhoneCall className="w-4 h-4" aria-hidden="true" />
        <span>{queue.length} في الانتظار</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-24 left-4 right-4 sm:left-auto sm:w-80 z-40 bg-paper border border-line rounded-xl shadow-lift overflow-hidden animate-fadeIn"
      role="region"
      aria-label="مكالمات الاستديو"
    >
      <header className="px-3 py-2 flex items-center justify-between border-b border-line bg-surface">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <PhoneCall className="w-3.5 h-3.5" aria-hidden="true" />
          </span>
          <span className="text-xs font-semibold text-ink truncate">
            مكالمات واردة
          </span>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          aria-label="طيّ اللوحة"
          className="w-7 h-7 rounded-md flex items-center justify-center text-ink-muted hover:text-ink hover:bg-ink/5 transition-colors"
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </header>

      <div className="p-3 space-y-3">
        {errorMessage && (
          <p role="alert" className="text-[11px] text-live bg-live/10 border border-live/25 rounded-md px-2.5 py-1.5 flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
            <span>{errorMessage}</span>
          </p>
        )}

        {/* الزملاء على الخط: من بقي متاحاً يعرف أن الطابور له */}
        {others.length > 0 && (
          <div className="space-y-1">
            {others.map((c) => (
              <p key={c.id} className="text-[11px] text-ink-muted flex items-center gap-1.5 truncate">
                <span className="w-1.5 h-1.5 rounded-full bg-live flex-shrink-0" aria-hidden="true" />
                <span className="truncate">
                  {c.answeredByName || 'زميلك'} يتحدّث مع {c.callerName}
                </span>
              </p>
            ))}
          </div>
        )}

        {/* الطابور */}
        {queue.length > 0 && (
          <div className="space-y-2">
            {queue.map((call, i) => (
              <div
                key={call.id}
                className="flex items-center gap-2 bg-surface border border-line rounded-md px-2.5 py-2"
              >
                <span className="text-[11px] text-ink-muted tabular-nums w-4 flex-shrink-0">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-ink truncate">{call.callerName}</div>
                  <div className="text-[11px] text-ink-muted truncate">
                    {call.topic || 'بلا موضوع محدّد'} · {since(call.createdAt)}
                  </div>
                  {call.callerPhone && (
                    <a
                      href={`tel:${call.callerPhone.replace(/[^+\d]/g, '')}`}
                      dir="ltr"
                      className="text-[11px] text-primary font-mono block truncate text-right"
                    >
                      {call.callerPhone}
                    </a>
                  )}
                </div>

                <button
                  onClick={() => line.acceptCall(call.id)}
                  title="ردّ"
                  aria-label={`ردّ على ${call.callerName}`}
                  className="w-8 h-8 rounded-md bg-primary text-primary-ink flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <PhoneCall className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => line.declineCall(call.id)}
                  aria-label={`اعتذار لـ ${call.callerName}`}
                  className="w-8 h-8 rounded-md border border-line bg-surface-alt text-ink-muted hover:text-live flex items-center justify-center flex-shrink-0 transition-colors"
                >
                  <PhoneOff className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};
